// src/pages/ReservationPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import { fetchAvailability } from '../api/availability';
import { useNavigate, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import ReservationModal from '../components/ReservationModal';
import NoticeContent from '../components/NoticeContent';
import StepIndicator from '../components/StepIndicator';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronRight, Info, Calendar as CalendarIcon, Clock } from 'lucide-react';
import { toast } from 'react-toastify';

/** 注意事項のバージョン（文面を更新したらここを更新。既存の同意は無効化され、再チェックが必要） */
const NOTICE_VERSION = 'v2025-10-01';

/** pill 用の Notice スコープ */
type PillScope = 'pill_all' | 'pill_low' | 'pill_shift' | 'pill_after';

/** 診療科目の選択肢 */
const serviceOptions = [
  { key: 'aga_male', i18nKey: 'reserve.services.aga_male', disabled: true },
  { key: 'aga_female', i18nKey: 'reserve.services.aga_female', disabled: true },
  { key: 'obesity_male', i18nKey: 'reserve.services.obesity_male' },
  { key: 'obesity_female', i18nKey: 'reserve.services.obesity_female' },
  { key: 'skincare', i18nKey: 'reserve.services.skincare' },
  { key: 'ed_premature', i18nKey: 'reserve.services.ed_premature' },
  { key: 'pill', i18nKey: 'reserve.services.pill' },
];

type TimeStatus = 'available' | 'limited' | 'full' | 'none';

interface TimeSlot {
  time: string;
  status: TimeStatus;
}

interface DayAvailability {
  date: string;
  slots: TimeSlot[];
}

/** ===== フォールバック文言（i18n 抜け/ブラウザ自動翻訳の影響を回避） ===== **/
const FALLBACK = {
  ja: {
    page_title: 'オンライン診療の予約',
    meta_description:
      'オンライン診療の予約ページです。診療科目の選択、初診／再診の選択、日時の選択ができます。',
    step1_title: '診療科目を選択',
    step1_notice_title: '注意事項の確認',
    step1_notice_checked: 'この診療項目の注意事項を確認しました。',
    view_notice_detail: '注意事項の詳細を見る',
    hide_notice_detail: '注意事項を閉じる',
    step2_title: '初診・再診を選択',
    step3_title: '日時を選択',
    first_time: '初診',
    follow_up: '再診',
    legend_available: '空きあり',
    legend_limited: '残りわずか',
    legend_full: '満枠',
    last_updated: '最終更新',
    auto_refresh: '自動更新',
    prev_week: '前の週',
    next_week: '次の週',
    time: '時間',
    no_slots: '現在、表示できる予約枠がありません。',
    loading: '読み込み中…',
    error_generic: 'エラーが発生しました',
    slot_just_taken: 'その時間帯は先ほど予約されました。別の時間をお選びください。',
    please_check_notice: 'この診療項目の注意事項をご確認のうえ、チェックを入れてください。',
  },
  zh: {
    page_title: '在线诊疗预约',
    meta_description:
      '在线诊疗预约页面。可以选择诊疗科目、初诊/复诊以及预约日期时间。',
    step1_title: '请选择诊疗科目',
    step1_notice_title: '注意事项确认',
    step1_notice_checked: '我已阅读并了解该诊疗项目的注意事项。',
    view_notice_detail: '查看注意事项详情',
    hide_notice_detail: '收起注意事项',
    step2_title: '请选择初诊或复诊',
    step3_title: '请选择日期与时间',
    first_time: '初诊',
    follow_up: '复诊',
    legend_available: '空闲',
    legend_limited: '名额紧张',
    legend_full: '已满',
    last_updated: '最后更新',
    auto_refresh: '自动刷新',
    prev_week: '上一周',
    next_week: '下一周',
    time: '时间',
    no_slots: '当前没有可显示的预约时段。',
    loading: '加载中…',
    error_generic: '发生错误',
    slot_just_taken: '该时段刚刚被预约了，请选择其他时间。',
    please_check_notice: '请先阅读该诊疗项目的注意事项并勾选。',
  },
} as const;

/** zh 系コード（zh, zh-CN 等）をまとめて zh として扱う */
const getLang = (i18nLang: string) =>
  String(i18nLang).toLowerCase().startsWith('zh') ? 'zh' : 'ja';

const safeT = (
  i18nLang: string,
  key: keyof typeof FALLBACK['ja'],
  tVal?: string | null
) => {
  if (typeof tVal === 'string' && tVal.trim()) return tVal.trim();
  return FALLBACK[getLang(i18nLang)][key];
};

const timeStatusMap: Record<TimeStatus, string> = {
  available: '○',
  limited: '△',
  full: '✕',
  none: '-',
};

/** 時刻の配列を生成（30分刻み） */
const generateTimeSlots = () => {
  const morning = [9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5];
  const afternoon = [13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5];
  const format = (num: number) =>
    `${String(Math.floor(num)).padStart(2, '0')}:${num % 1 === 0 ? '00' : '30'}`;
  return [...morning, ...afternoon].map(format);
};

/** 任意の "H:MM" / "H:MM:SS" を "HH:MM" へ統一 */
const toHHMM = (s: string) => {
  if (!s && s !== '') return '';
  const parts = String(s).trim().split(':');
  const h = parts.length > 0 ? Number(parts[0]) : 0;
  const m = parts.length > 1 ? Number(parts[1]) : 0;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** バックエンドの status をフロント用 TimeStatus に正規化 */
const normalizeStatus = (raw: any): TimeStatus => {
  if (!raw && raw !== '') return 'none';
  const s = String(raw).toLowerCase();
  if (s === 'available') return 'available';
  if (s === 'limited' || s === 'few') return 'limited';
  if (s === 'full' || s === 'unavailable') return 'full';
  if (s === 'none' || s === 'no' || s === 'empty') return 'none';
  return 'none';
};

type LocState =
  | {
    preselectService?: string;
    preAgreed?: boolean;
    from?: 'nav' | 'service';
    pillNoticeScope?: PillScope;
  }
  | undefined;
type TimePeriod = 'morning' | 'afternoon' | 'evening' | 'all';


const ReservationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedService, setSelectedService] = useState('');
  const [visitType, setVisitType] = useState<'first' | 'followup' | ''>('');
  const isFirst = visitType === 'first';
  const isFollowup = visitType === 'followup';
  const [calendarData, setCalendarData] = useState<DayAvailability[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [selectedSlot, setSelectedSlot] = useState<{ date: string; time: string } | null>(null);
  const [showModal, setShowModal] = useState(false);

  // 自動リフレッシュ & 最終更新時刻
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [selectedDateIdx, setSelectedDateIdx] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('all');


  /** サービス毎の「注意事項を確認済み」状態（key = `${service}:${NOTICE_VERSION}` or pill は scope まで） */
  const [consentMap, setConsentMap] = useState<Record<string, boolean>>({});

  /** pill の Notice スコープ（デフォルトは三合一） */
  const [pillScope, setPillScope] = useState<PillScope>('pill_all');

  /** 注意事項の本文の折りたたみ状態（Navbar→展開 / 紹介ページ→折りたたみ） */
  const initFrom = (location.state as LocState)?.from;
  const [noticeOpen, setNoticeOpen] = useState<boolean>(initFrom === 'service' ? false : true);

  /** 言語ロケール適用 */
  useEffect(() => {
    const newLocale = getLang(i18n.language) === 'zh' ? 'zh-cn' : 'ja';
    dayjs.locale(newLocale);
  }, [i18n.language]);

  // スクロール用
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // スクロール位置の監視（矢印の表示制御）
  const updateScrollButtons = () => {
    const el = tableContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    updateScrollButtons();
    // データの読み込み直後はレイアウト確定まで少し時間がかかる場合があるため、遅延実行も追加
    const timer = setTimeout(updateScrollButtons, 500);

    window.addEventListener('resize', updateScrollButtons);
    return () => {
      window.removeEventListener('resize', updateScrollButtons);
      clearTimeout(timer);
    };
  }, [calendarData, loading]);

  const scrollTable = (direction: 'left' | 'right') => {
    const el = tableContainerRef.current;
    if (!el) return;
    const distance = 400;
    const targetScroll = el.scrollLeft + (direction === 'left' ? -distance : distance);
    el.scrollTo({ left: targetScroll, behavior: 'smooth' });
  };

  /** consentMap のキー生成（pill は scope まで区別） */
  function consentKeyFor(svc: string, scope?: PillScope) {
    if (svc === 'pill') return `${svc}:${NOTICE_VERSION}:${scope || 'pill_all'}`;
    return `${svc}:${NOTICE_VERSION}`;
  }

  /** 現サービスの注意事項チェック状態（pill は scope まで見る） */
  function isConsentedFor(svc: string | null, map: Record<string, boolean>, scope?: PillScope) {
    if (!svc) return false;
    return !!map[consentKeyFor(svc, scope)];
  }

  /** 初期化：クエリ & state 読み込み（プリセット） */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const svc = params.get('svc');
    const vt = params.get('vt') as 'first' | 'followup' | null;
    if (svc && serviceOptions.some(s => s.key === svc)) setSelectedService(svc);
    if (vt === 'first' || vt === 'followup') setVisitType(vt);

    const state = (location.state || {}) as LocState;
    if (state?.pillNoticeScope) {
      setPillScope(state.pillNoticeScope);
    }
    if (state?.preselectService && serviceOptions.some(s => s.key === state.preselectService)) {
      setSelectedService(state.preselectService);
      if (state.preAgreed) {
        // pill の場合は scope ごとに同意を点灯
        const k = consentKeyFor(
          state.preselectService,
          state.preselectService === 'pill' ? (state.pillNoticeScope || 'pill_all') : undefined
        );
        setConsentMap(prev => ({ ...prev, [k]: true }));
      }
    }
    // デフォルト時段の決定


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** svc/vt の URL 同期（共有・再読込しても保持） */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let changed = false;
    if (selectedService) {
      params.set('svc', selectedService);
      changed = true;
    } else if (params.has('svc')) {
      params.delete('svc');
      changed = true;
    }
    if (visitType) {
      params.set('vt', visitType);
      changed = true;
    } else if (params.has('vt')) {
      params.delete('vt');
      changed = true;
    }
    if (changed) {
      setSelectedDateIdx(0); // Reset date choice on change
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, visitType]);

  const allTimes = useMemo(() => generateTimeSlots(), []);
  const times = useMemo(() => {
    return allTimes;
  }, [allTimes]);

  const now = dayjs();

  const isPastSlot = (dateStr: string, timeStr: string) => {
    const [h, m] = toHHMM(timeStr).split(':').map(Number);
    const dt = dayjs(dateStr).hour(h).minute(m).second(0);
    return dt.isBefore(now);
  };

  /** 可用枠の取得 */
  const loadAvailability = async () => {
    const today = dayjs();
    const startStr = today.format('YYYY-MM-DD');
    // 「次々週の日曜日」までの日数を計算。
    const endDate = today.day() === 0 ? today.day(7) : today.day(14);
    const diff = endDate.diff(today, 'day');
    const daysCount = diff + 1;

    try {
      setLoading(true);
      setLoadErr(null);
      const data = await fetchAvailability(startStr, daysCount);

      // time -> HH:MM / status -> TimeStatus へ正規化
      const fixed: DayAvailability[] = Array.isArray(data)
        ? data.map((day: any) => {
          const slots = Array.isArray(day.slots)
            ? day.slots.map((s: any) => ({
              time: toHHMM(String(s.time)),
              status: normalizeStatus(s.status),
            }))
            : [];
          return { date: String(day.date), slots };
        })
        : [];

      setCalendarData(fixed);
      setLastRefreshedAt(dayjs().format('HH:mm:ss'));
    } catch (e) {
      console.error('予約情報の取得に失敗しました', e);
      setLoadErr('failed');
    } finally {
      setLoading(false);
    }
  };

  /** 「科目選択」＋「注意事項チェック」＋「初診/再診選択」で読み込み開始 */
  useEffect(() => {
    const consentOk = isConsentedFor(
      selectedService || null,
      consentMap,
      selectedService === 'pill' ? pillScope : undefined
    );
    if (selectedService && consentOk && visitType) {
      loadAvailability();
    } else {
      // 条件を満たさない場合は古いデータを隠す
      setCalendarData([]);
      setLastRefreshedAt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, consentMap, visitType, pillScope]);

  /** 30秒ごとの自動更新（条件を満たしたときのみ） */
  useEffect(() => {
    const consentOk = isConsentedFor(
      selectedService || null,
      consentMap,
      selectedService === 'pill' ? pillScope : undefined
    );
    if (!autoRefresh || !selectedService || !consentOk || !visitType) return;
    const id = setInterval(() => {
      loadAvailability();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, selectedService, consentMap, visitType, pillScope]);

  useEffect(() => {
    if (showModal) setAutoRefresh(false);
  }, [showModal]);

  const formatWeekday = (dateStr: string) => {
    const dayIndex = dayjs(dateStr).day();
    const zhWeek = ['日', '一', '二', '三', '四', '五', '六'];
    const jaWeek = ['日', '月', '火', '水', '木', '金', '土'];
    return getLang(i18n.language) === 'zh' ? `周${zhWeek[dayIndex]}` : jaWeek[dayIndex];
  };

  /** 直前確認（他ユーザーと取り合い時の衝突対策） */
  const verifySlotStillAvailable = async (date: string, time: string) => {
    try {
      const start = date; // 当日のみ再取得
      const dayDataArr = await fetchAvailability(start, 1);
      const dayData = Array.isArray(dayDataArr) ? dayDataArr[0] : null;
      if (!dayData || !Array.isArray(dayData.slots)) return false;
      const matchRaw = dayData.slots.find((s: any) => toHHMM(String(s.time)) === toHHMM(time));
      if (!matchRaw) return false;
      const status = normalizeStatus((matchRaw as any).status);
      return status === 'available' || status === 'limited';
    } catch (err) {
      console.error('verifySlotStillAvailable error', err);
      return false;
    }
  };

  const onSlotClick = async (status: string, date: string, time: string) => {
    if (isPastSlot(date, time)) return;
    const allowedByStatus = status === 'available' || status === 'limited';
    if (!allowedByStatus) return;

    const token = localStorage.getItem('token');
    if (!token) {
      const redirect = `${location.pathname}?svc=${encodeURIComponent(selectedService)}&vt=${encodeURIComponent(
        visitType
      )}`;
      navigate(`/login?redirectTo=${encodeURIComponent(redirect)}`);
      return;
    }

    const stillOk = await verifySlotStillAvailable(date, time);
    if (!stillOk) {
      toast.warn(safeT(i18n.language, 'slot_just_taken', t('reserve.slot_just_taken')), { autoClose: 2500 });
      loadAvailability();
      return;
    }

    const slot = { date, time: toHHMM(time) };
    setSelectedSlot(slot);
    setShowModal(true);
  };

  /** サービス選択時（visitType は保持。pill を選んだら scope は三合一にリセット） */
  const handleServiceSelect = (svc: string) => {
    setSelectedService(svc);
    if (svc === 'pill') setPillScope('pill_all');
  };

  /** チェック切り替え（本文の下に常時表示） */
  const toggleConsent = (checked: boolean) => {
    if (!selectedService) return;
    const key = consentKeyFor(selectedService, selectedService === 'pill' ? pillScope : undefined);
    setConsentMap(prev => ({ ...prev, [key]: checked }));
  };

  /** Helmet: 画面タイトルとメタ説明 */
  const svcLabel = selectedService ? t(`reserve.services.${selectedService}`, { defaultValue: '' }) : '';
  const vtLabel =
    visitType === 'first'
      ? t('reserve.first_time', { defaultValue: FALLBACK.ja.first_time })
      : visitType === 'followup'
        ? t('reserve.follow_up', { defaultValue: FALLBACK.ja.follow_up })
        : '';
  const baseTitle = safeT(i18n.language, 'page_title', t('reserve.page_title'));
  const pageTitle = [baseTitle, svcLabel, vtLabel, 'Melodis Clinic'].filter(Boolean).join(' - ');

  /** NoticeContent に渡す serviceKey（pill は scope を使う） */
  const noticeServiceKey = selectedService === 'pill' ? pillScope : selectedService;

  return (
    <div className="min-h-screen pt-12 sm:pt-16 pb-12 bg-background font-sans" translate="no">
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={safeT(i18n.language, 'meta_description', t('reserve.meta_description'))}
        />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <StepIndicator 
          steps={[
            t('reserve.step1_title_short', '科目'), 
            t('reserve.step2_title_short', '初再診'), 
            t('reserve.step3_title_short', '日時')
          ]} 
          currentStep={selectedService && isConsentedFor(selectedService, consentMap, selectedService === 'pill' ? pillScope : undefined) && visitType ? 2 : selectedService && isConsentedFor(selectedService, consentMap, selectedService === 'pill' ? pillScope : undefined) ? 1 : 0}
        />

        <AnimatePresence mode="wait">
          {!selectedService || !isConsentedFor(selectedService, consentMap, selectedService === 'pill' ? pillScope : undefined) ? (
            <motion.section 
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="mb-10"
            >
              <h2 className="text-xl font-bold text-navy mb-6 text-center" translate="no">
                {safeT(i18n.language, 'step1_title', t('reserve.step1_title'))}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 max-w-2xl mx-auto">
                {serviceOptions.map((option) => (
                  <button
                    key={option.key}
                    onClick={() => {
                        if (!(option as any).disabled) handleServiceSelect(option.key);
                    }}
                    className={classNames(
                      'group relative overflow-hidden p-4 sm:p-6 rounded-[2rem] border-2 transition-all duration-300 flex flex-col items-center justify-center min-h-[100px] text-center shadow-sm w-full max-w-[140px] sm:max-w-[180px] mx-auto',
                      (option as any).disabled
                        ? 'bg-slate-50 border-slate-100 opacity-60 grayscale cursor-not-allowed text-slate-400'
                        : selectedService === option.key
                          ? 'bg-coral/5 text-coral border-coral ring-4 ring-coral/10 scale-105'
                          : 'bg-white text-slate-700 border-slate-100 hover:border-coral/20 hover:bg-slate-50 active:scale-95'
                    )}
                  >
                    {selectedService === option.key && (
                      <motion.div 
                        layoutId="choice-indicator"
                        className="absolute top-3 right-3"
                      >
                        <CheckCircle2 className="w-5 h-5 text-coral" />
                      </motion.div>
                    )}
                    <span className="text-base font-bold" translate="no">{t(option.i18nKey)}</span>
                  </button>
                ))}
              </div>

              {selectedService && (
                <motion.div 
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="mt-8 overflow-hidden"
                >
                  <div className="rounded-[2.5rem] border-2 border-slate-100 bg-white p-6 md:p-8 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-navy/5 flex items-center justify-center">
                          <Info className="w-5 h-5 text-navy" />
                        </div>
                        <h3 className="text-lg font-bold text-navy">
                          {safeT(i18n.language, 'step1_notice_title', t('reserve.step1_notice_title'))}
                        </h3>
                      </div>

                      <button
                        type="button"
                        onClick={() => setNoticeOpen((v) => !v)}
                        className="inline-flex items-center gap-1 text-sm font-bold text-coral hover:opacity-80 transition"
                      >
                        {noticeOpen
                          ? safeT(i18n.language, 'hide_notice_detail', t('reserve.hide_notice_detail'))
                          : safeT(i18n.language, 'view_notice_detail', t('reserve.view_notice_detail'))}
                        <ChevronRight className={classNames("w-4 h-4 transition-transform", noticeOpen && "rotate-90")} />
                      </button>
                    </div>

                    {noticeOpen && (
                      <div className="bg-slate-50/50 rounded-2xl p-6 mb-6 overflow-y-auto max-h-[300px]">
                        {selectedService === 'pill' && noticeServiceKey === 'pill_all' ? (
                          <div className="space-y-6">
                            <NoticeContent service="pill_low" lang={i18n.language} version={NOTICE_VERSION} />
                            <NoticeContent service="pill_shift" lang={i18n.language} version={NOTICE_VERSION} />
                            <NoticeContent service="pill_after" lang={i18n.language} version={NOTICE_VERSION} />
                          </div>
                        ) : (
                          <NoticeContent
                            service={noticeServiceKey}
                            lang={i18n.language}
                            version={NOTICE_VERSION}
                          />
                        )}
                      </div>
                    )}

                    <label className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100 cursor-pointer active:scale-[0.98] transition-transform">
                      <input
                        type="checkbox"
                        className="w-5 h-5 rounded border-slate-300 text-coral focus:ring-coral cursor-pointer"
                        checked={isConsentedFor(
                          selectedService,
                          consentMap,
                          selectedService === 'pill' ? pillScope : undefined
                        )}
                        onChange={(e) => toggleConsent(e.target.checked)}
                      />
                      <span className="text-sm font-bold text-slate-600">
                        {safeT(i18n.language, 'step1_notice_checked', t('reserve.step1_notice_checked'))}
                      </span>
                    </label>
                  </div>
                </motion.div>
              )}
            </motion.section>
          ) : !visitType ? (
            <motion.section 
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="mb-14"
            >
              <div className="flex items-center gap-2 mb-6 justify-center">
                <button onClick={() => setSelectedService('')} className="text-slate-400 hover:text-navy transition font-bold text-sm">
                  ← {t('reserve.back_to_service', '科目選択に戻る')}
                </button>
              </div>
              <h2 className="text-xl font-bold text-navy mb-6 text-center" translate="no">
                {safeT(i18n.language, 'step2_title', t('reserve.step2_title'))}
              </h2>
              <div className="flex flex-col sm:flex-row gap-6 max-w-sm mx-auto">
                <button
                  onClick={() => setVisitType('first')}
                  className={classNames(
                    'flex-1 p-6 rounded-[1.5rem] border-2 transition-all duration-300 flex flex-col items-center gap-3 text-center shadow-sm',
                    isFirst
                      ? 'bg-coral/5 text-coral border-coral ring-4 ring-coral/10'
                      : 'bg-white text-slate-600 border-slate-100 hover:bg-slate-50 active:scale-95'
                  )}
                >
                  <div className={classNames(
                    "w-12 h-12 rounded-full flex items-center justify-center font-black",
                    isFirst ? "bg-coral text-white" : "bg-slate-100 text-slate-400"
                  )}>1</div>
                  <span className="text-lg font-black" translate="no">
                    {safeT(i18n.language, 'first_time', t('reserve.first_time'))}
                  </span>
                </button>
                <button
                  onClick={() => setVisitType('followup')}
                  className={classNames(
                    'flex-1 p-6 rounded-[1.5rem] border-2 transition-all duration-300 flex flex-col items-center gap-3 text-center shadow-sm',
                    isFollowup
                      ? 'bg-coral/5 text-coral border-coral ring-4 ring-coral/10'
                      : 'bg-white text-slate-600 border-slate-100 hover:bg-slate-50 active:scale-95'
                  )}
                >
                  <div className={classNames(
                    "w-12 h-12 rounded-full flex items-center justify-center font-black",
                    isFollowup ? "bg-coral text-white" : "bg-slate-100 text-slate-400"
                  )}>2+</div>
                  <span className="text-lg font-black" translate="no">
                    {safeT(i18n.language, 'follow_up', t('reserve.follow_up'))}
                  </span>
                </button>
              </div>
            </motion.section>
          ) : (
            <motion.section 
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="mb-10"
            >
              <div className="flex items-center gap-2 mb-4 justify-center">
                <button onClick={() => setVisitType('')} className="text-slate-400 hover:text-navy transition font-bold text-xs flex items-center gap-1">
                   <ChevronRight className="w-3 h-3 rotate-180" />
                   {t('reserve.back_to_visittype', '初再診選択に戻る')}
                </button>
              </div>

              <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-navy/5 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-navy">{t('reserve.step3_title_short', '希望日時を選択')}</h3>
                    <p className="text-[10px] text-slate-400 font-bold">{t('reserve.all_day_slots', '全時間帯を表示中')}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between w-full sm:w-auto gap-6">
                  <div className="flex items-center gap-3 text-[11px] font-bold">
                    <span className="flex items-center gap-1 text-emerald-500" translate="no">○ {safeT(i18n.language, 'legend_available', t('reserve.legend_available'))}</span>
                    <span className="flex items-center gap-1 text-amber-500" translate="no">△ {safeT(i18n.language, 'legend_limited', t('reserve.legend_limited'))}</span>
                    <span className="flex items-center gap-1 text-slate-300" translate="no">✕ {safeT(i18n.language, 'legend_full', t('reserve.legend_full'))}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold">
                    <span>{lastRefreshedAt || '--:--:--'}</span>
                    <label className="flex items-center gap-1 cursor-pointer text-navy">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 rounded-sm border-slate-300 text-navy focus:ring-navy"
                        checked={autoRefresh}
                        onChange={(e) => setAutoRefresh(e.target.checked)}
                      />
                      <span className="sr-only">Auto</span>
                    </label>
                  </div>
                </div>
              </div>


              {!loading && !loadErr && (
                <div className="flex flex-col gap-6">
                  {/* デスクトップ 表形式 */}
                  <div className="relative group hidden md:block">
                    {canScrollLeft && (
                      <button 
                        onClick={() => scrollTable('left')}
                        className="absolute left-[60px] top-[74px] bottom-0 z-40 w-10 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] border-r border-slate-100 shadow-[4px_0_12px_rgba(0,0,0,0.03)] text-slate-400 hover:text-coral hover:bg-white/90 transition-all opacity-0 group-hover:opacity-100 rounded-bl-[2rem]"
                      >
                        <ChevronRight className="w-6 h-6 rotate-180" />
                      </button>
                    )}
                    {canScrollRight && (
                      <button 
                        onClick={() => scrollTable('right')}
                        className="absolute right-0 top-[74px] bottom-0 z-40 w-10 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] border-l border-slate-100 shadow-[-4px_0_12px_rgba(0,0,0,0.03)] text-coral hover:bg-white/90 transition-all opacity-0 group-hover:opacity-100 rounded-br-[2rem]"
                      >
                        <motion.div animate={{ x: [0, 3, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                          <ChevronRight className="w-6 h-6" />
                        </motion.div>
                      </button>
                    )}
                    
                  <div 
                    ref={tableContainerRef}
                    onScroll={updateScrollButtons}
                    className="overflow-x-auto bg-white rounded-[2rem] border border-slate-100 shadow-sm max-w-full scrollbar-hide"
                  >
                    
                    
                    

                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          {/* 时间列 - 固定左侧 */}
                          <th className="sticky left-0 z-30 bg-slate-100 border-b p-4 text-center text-navy font-black text-xs w-[60px] min-w-[60px]" translate="no">
                            {safeT(i18n.language, 'time', t('reserve.time'))}
                          </th>
                          
                          {calendarData.map((day) => {
                            const isSun = dayjs(day.date).day() === 0;
                            const isSat = dayjs(day.date).day() === 6;
                            return (
                              <th key={day.date} className={classNames(
                                "border-b p-4 text-center min-w-[95px]",
                                isSun ? "bg-red-50/50" : isSat ? "bg-blue-50/50" : "bg-slate-50/50"
                              )} translate="no">
                                <div className={classNames(
                                  "text-sm font-black",
                                  isSun ? "text-red-500" : isSat ? "text-blue-500" : "text-navy"
                                )}>
                                  {dayjs(day.date).format('M/DD')}
                                </div>
                                <div className={classNames(
                                  "text-xs font-bold",
                                  isSun ? "text-red-500" : isSat ? "text-blue-500" : "text-slate-400"
                                )}>
                                  ({formatWeekday(day.date)})
                                </div>
                              </th>
                            );
                          })}
                          {/* 末尾スペーサー */}
                          <th className="min-w-[40px]" />
                        </tr>
                      </thead>
                      <tbody>
                        {times.map((time) => (
                          <tr key={time} className="hover:bg-slate-50/50 transition-colors">
                            <td className="sticky left-0 z-10 border-b p-3 text-center font-black text-navy text-[10px] bg-white w-[60px] min-w-[60px]" translate="no">
                              {time}
                            </td>
                            
                            {calendarData.map((day) => {
                              const slot = day.slots.find((s) => s.time === time);
                              const status = slot ? slot.status : 'none';
                              const clickable =
                                (status === 'available' || status === 'limited') && !isPastSlot(day.date, time);

                              const cellTextColor =
                                status === 'available'
                                  ? 'text-emerald-500'
                                  : status === 'limited'
                                    ? 'text-amber-500'
                                    : status === 'full'
                                      ? 'text-slate-300'
                                      : 'text-slate-200';
                              const symbol = timeStatusMap[status] || '-';

                              return (
                                <td
                                  key={`${day.date}-${time}`}
                                  className={classNames(
                                    'border-b p-2 text-center transition-all',
                                    clickable ? 'cursor-pointer hover:bg-navy/5 active:bg-navy/10' : 'cursor-not-allowed opacity-40'
                                  )}
                                  onClick={() => clickable && onSlotClick(status, day.date, time)}
                                  translate="no"
                                >
                                  <div className="flex flex-col items-center justify-center group relative h-full">
                                    <span className={classNames('text-2xl font-black leading-none mb-1', cellTextColor)}>
                                      {symbol}
                                    </span>
                                    {clickable && (
                                       <div className="w-5 h-0.5 bg-emerald-500/30 rounded-full" />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                            
                            <td className="min-w-[40px]" />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </div>

                  {/* モバイル 日付選択 + 时间段展示 */}
                  <div className="block md:hidden space-y-6">
                    {/* 日付セレクター */}
                    <div className="flex overflow-x-auto gap-2 pt-2 pb-4 scrollbar-hide -mx-4 px-4 sticky top-16 z-10 bg-white/80 backdrop-blur-md">
                      {calendarData.map((day, idx) => {
                        const isSelected = selectedDateIdx === idx;
                        const isSun = dayjs(day.date).day() === 0;
                        const isSat = dayjs(day.date).day() === 6;
                        const showMonth = idx === 0 || dayjs(day.date).date() === 1;
                        
                        return (
                          <div key={day.date} className="flex flex-col gap-1 items-center">
                            {showMonth && (
                              <span className="text-[10px] font-bold text-navy absolute -top-3.5 bg-white px-1 rounded truncate max-w-[40px]">
                                {dayjs(day.date).format('M月')}
                              </span>
                            )}
                            <button
                              onClick={() => setSelectedDateIdx(idx)}
                              className={classNames(
                                "shrink-0 w-12 h-16 rounded-2xl border-2 flex flex-col items-center justify-center transition-all",
                                isSelected
                                  ? "bg-coral border-coral text-white shadow-lg scale-105"
                                  : "bg-white border-slate-100 text-slate-400 hover:border-slate-200"
                              )}
                            >
                              <span className={classNames(
                                "text-[10px] font-black mb-0.5",
                                isSelected ? "text-white/60" : (isSun ? "text-red-400" : isSat ? "text-blue-400" : "text-slate-400")
                              )}>
                                {formatWeekday(day.date).slice(-1)}
                              </span>
                              <span className="text-base font-black leading-none">{dayjs(day.date).format('D')}</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {calendarData[selectedDateIdx] && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        key={calendarData[selectedDateIdx].date}
                        className="bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-6" translate="no">
                          <div>
                            <div className="text-lg font-black text-navy flex items-center gap-2">
                              {dayjs(calendarData[selectedDateIdx].date).format('M月D日')}
                              <span className={classNames(
                                "text-xs px-2 py-0.5 rounded-full border",
                                dayjs(calendarData[selectedDateIdx].date).day() === 0 ? "bg-red-50 text-red-500 border-red-100" :
                                dayjs(calendarData[selectedDateIdx].date).day() === 6 ? "bg-blue-50 text-blue-500 border-blue-100" :
                                "bg-slate-50 text-slate-500 border-slate-100"
                              )}>
                                {formatWeekday(calendarData[selectedDateIdx].date)}
                              </span>
                            </div>
                            <div className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
                              {calendarData[selectedDateIdx].slots.filter(s => s.status === 'available' || s.status === 'limited').length} Slots available
                            </div>
                          </div>
                          <div className="flex gap-2">
                             <button
                               onClick={() => setSelectedDateIdx(prev => Math.max(0, prev - 1))}
                               disabled={selectedDateIdx === 0}
                               className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-30"
                             >
                               <ChevronRight className="w-5 h-5 rotate-180" />
                             </button>
                             <button
                               onClick={() => setSelectedDateIdx(prev => Math.min(calendarData.length - 1, prev + 1))}
                               disabled={selectedDateIdx === calendarData.length - 1}
                               className="w-10 h-10 rounded-full border border-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-30"
                             >
                               <ChevronRight className="w-5 h-5" />
                             </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-2">
                          {calendarData[selectedDateIdx].slots
                            .map((slot: TimeSlot) => {
                              const status: TimeStatus = slot.status || 'none';
                              const symbol = timeStatusMap[status] || '-';
                              const disabled =
                                !(status === 'available' || status === 'limited') || isPastSlot(calendarData[selectedDateIdx].date, slot.time);
                              
                              const slotColor = 
                                status === 'available' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' :
                                status === 'limited' ? 'bg-amber-50 text-amber-600 border-amber-100' :
                                'bg-slate-50 text-slate-300 border-slate-100';

                              return (
                                <motion.button
                                  whileTap={{ scale: 0.95 }}
                                  key={slot.time}
                                  onClick={() => onSlotClick(status, calendarData[selectedDateIdx].date, slot.time)}
                                  disabled={disabled}
                                  className={classNames(
                                    'flex flex-col items-center justify-center p-2 rounded-xl border transition-all font-bold text-sm',
                                    disabled ? 'opacity-40 grayscale cursor-not-allowed' : `shadow-sm cursor-pointer ${slotColor} hover:border-current`
                                  )}
                                  translate="no"
                                >
                                  <span className="text-[9px] opacity-70 mb-0.5 font-sans">{toHHMM(slot.time)}</span>
                                  <span className="text-base font-black">{symbol}</span>
                                </motion.button>
                              );
                            })}
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {calendarData.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] border border-slate-100" translate="no">
                      <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CalendarIcon className="w-8 h-8 text-slate-300" />
                      </div>
                      <p className="text-slate-400 font-bold">
                        {safeT(i18n.language, 'no_slots', t('reserve.no_slots'))}
                      </p>
                    </div>
                  )}
                  </div>
              )}

              {(loading || loadErr) && (
                <div className="flex flex-col items-center justify-center py-24 bg-white rounded-[2.5rem] border border-slate-100">
                  {loading ? (
                    <>
                      <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-12 h-12 border-4 border-navy/10 border-t-navy rounded-full mb-4"
                      />
                      <p className="text-slate-400 font-bold" translate="no">
                        {safeT(i18n.language, 'loading', t('reserve.loading'))}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                        <Info className="w-8 h-8 text-red-400" />
                      </div>
                      <p className="text-red-400 font-bold" translate="no">
                        {safeT(i18n.language, 'error_generic', t('reserve.error_generic'))}
                      </p>
                    </>
                  )}
                </div>
              )}
            </motion.section>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showModal && selectedSlot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setShowModal(false)} />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg"
            >
              <ReservationModal
                selectedSlot={selectedSlot}
                service={selectedService}
                visitType={visitType}
                onClose={() => {
                  setShowModal(false);
                  setSelectedSlot(null);
                }}
                onSuccess={() => {
                  setShowModal(false);
                  setSelectedSlot(null);
                  navigate('/mypage');
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReservationPage;
